import { DriverData, FormulaMonthlyBudget } from '../types/driverData'
import { StatementDataType } from '../../financialStatements/types'
import { extractMonthlyValuesFromRegistry } from '../../../shared/utils'

interface ConvertToFormulaDataOptions {
  /**
   * Grid row data with modified values in newBudgets/newActuals registries
   */
  rowData: DriverData[]
}

/**
 * Converts modified grid data to FormulaMonthlyBudget array for saving to backend.
 * Processes both newBudgets and newActuals registries from each row.
 *
 * @param options - Conversion options
 * @returns Array of FormulaMonthlyBudget objects representing all modified budget and actuals cells
 *
 * @example
 * ```typescript
 * const formulaData = convertToFormulaData({
 *   rowData: gridApi.getRenderedNodes().map(node => node.data)
 * })
 * // Result: [
 * //   { formulaId: 1, dataType: 'budget', year: 2024, month: 1, value: 1000, dimensionId: 'dim1' },
 * //   { formulaId: 1, dataType: 'actuals', year: 2024, month: 1, value: 950, dimensionId: 'dim1' }
 * // ]
 * ```
 */
export const convertToFormulaData = ({ rowData }: ConvertToFormulaDataOptions): FormulaMonthlyBudget[] => {
  const formulaDataList: FormulaMonthlyBudget[] = []

  // Process each row
  rowData.forEach(row => {
    const { formulaId, dimensionId, newBudgets, newActuals } = row

    // Normalize dimensionId: convert "null" string to null (enterprise level)
    const normalizedDimensionId = dimensionId === 'null' ? null : dimensionId

    // Process both budget and actuals registries
    const registries = [
      { registry: newBudgets, dataType: StatementDataType.budget },
      { registry: newActuals, dataType: StatementDataType.actuals }
    ]

    registries.forEach(({ registry, dataType }) => {
      const values = extractMonthlyValuesFromRegistry(registry, (year, month, value) => ({
        formulaId,
        year,
        month,
        value,
        dimensionId: normalizedDimensionId,
        dataType
      }))
      formulaDataList.push(...values)
    })
  })

  return formulaDataList
}
